import { CartUpdateEvent, ThemeEvents } from '@theme/events';
import {
  PURCHASE_LIMIT_PER_VARIANT,
  PURCHASE_MESSAGES,
  beginPurchaseAction,
  endPurchaseAction,
  normalizePurchaseMessage,
  notifyPurchase,
} from './velzeen-purchase-ui.js';

export { notifyPurchase } from './velzeen-purchase-ui.js';

const PURCHASE_ROOT = '.buy-buttons-block[data-pdp-purchase]';
const INITIAL_CART_SYNC_IDLE_TIMEOUT_MS = 1800;
const CART_SYNC_RETRY_DELAY_MS = 1500;
const states = new WeakMap();

const number = (value, fallback = 0) => {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : fallback;
};

const shopRoot = () => window.Shopify?.routes?.root || '/';
const quantityInput = (root) => root.querySelector('[data-quantity-input]');
const selectedQuantity = (root) => Math.max(1, Math.floor(number(quantityInput(root)?.value, 1)));
const selectedVariantId = (root) => number(root.dataset.variantId);
const isAvailable = (root) => root.dataset.variantAvailable === 'true';
const isInventoryTracked = (root) => root.dataset.inventoryTracked === 'true';
const inventoryQuantity = (root) => Math.max(0, number(root.dataset.inventoryQuantity));

function setDisabled(element, disabled, nativeDisabled = disabled) {
  if (!(element instanceof HTMLElement)) return;
  element.setAttribute('aria-disabled', String(disabled));
  element.tabIndex = disabled && nativeDisabled ? -1 : 0;
  if (element instanceof HTMLButtonElement) element.disabled = disabled && nativeDisabled;
}

function cartQuantity(root) {
  const state = states.get(root);
  return state?.cartReady ? Math.max(0, number(state.cartQuantity)) : 0;
}

function inventoryRemaining(root) {
  if (!isInventoryTracked(root)) return null;

  const inventory = inventoryQuantity(root);
  if (inventory > 0) return Math.max(0, inventory - cartQuantity(root));

  // A tracked variant can remain available with zero inventory when Shopify
  // is configured to continue selling. In that case there is no known maximum.
  return isAvailable(root) ? null : 0;
}

function purchaseLimitRemaining(root) {
  return Math.max(0, PURCHASE_LIMIT_PER_VARIANT - cartQuantity(root));
}

function maximumSelectable(root) {
  const maximums = [inventoryRemaining(root), purchaseLimitRemaining(root)].filter(Number.isFinite);
  return maximums.length ? Math.max(0, Math.min(...maximums)) : null;
}

function maximumReachedMessage(root) {
  const stockRemaining = inventoryRemaining(root);
  const limitRemaining = purchaseLimitRemaining(root);

  if (stockRemaining !== null && stockRemaining < limitRemaining) return PURCHASE_MESSAGES.sold;
  return PURCHASE_MESSAGES.limit;
}

function setQuantity(root, value, { emitChange = true } = {}) {
  const input = quantityInput(root);
  if (!(input instanceof HTMLInputElement)) return;

  const maximum = maximumSelectable(root);
  const requested = Math.max(1, Math.floor(number(value, 1)));
  const normalized = Number.isFinite(maximum) && maximum > 0
    ? Math.min(maximum, requested)
    : requested;
  const previousValue = input.value;

  input.value = String(normalized);
  input.setAttribute('aria-valuemin', '1');
  input.setAttribute('aria-valuenow', input.value);

  if (Number.isFinite(maximum) && maximum > 0) {
    input.setAttribute('aria-valuemax', String(maximum));
  } else {
    input.removeAttribute('aria-valuemax');
  }

  if (emitChange && input.value !== previousValue) {
    input.dispatchEvent(new Event('change', { bubbles: true }));
  }
}

function normalize(root) {
  const state = states.get(root);
  const quantity = selectedQuantity(root);
  const maximum = maximumSelectable(root);
  const hardDisabled = !isAvailable(root) || state?.busy === true;
  const maximumReached = Number.isFinite(maximum) && quantity >= maximum;

  setDisabled(root.querySelector('[data-quantity-action="decrease"]'), hardDisabled || quantity <= 1);

  // Keep the maximum-reached plus control focusable so a deliberate click can
  // explain the limit without sending an invalid cart request.
  setDisabled(
    root.querySelector('[data-quantity-action="increase"]'),
    hardDisabled || maximumReached,
    hardDisabled
  );

  for (const button of root.querySelectorAll('[data-purchase-action]')) {
    setDisabled(button, hardDisabled);
  }

  const input = quantityInput(root);
  if (input) {
    input.setAttribute('aria-valuemin', '1');
    input.setAttribute('aria-valuenow', String(quantity));

    if (Number.isFinite(maximum) && maximum > 0) {
      input.setAttribute('aria-valuemax', String(maximum));
    } else {
      input.removeAttribute('aria-valuemax');
    }
  }
}

async function fetchCart() {
  const response = await fetch(`${shopRoot()}cart.js`, {
    credentials: 'same-origin',
    headers: { Accept: 'application/json' },
  });
  if (!response.ok) throw new Error(PURCHASE_MESSAGES.addError);
  return response.json();
}

const quantityInBag = (cart, variantId) => (cart.items || []).reduce(
  (total, item) => (number(item.variant_id) === variantId ? total + number(item.quantity) : total),
  0
);

function afterCorePaint(callback) {
  const frame = typeof window.requestAnimationFrame === 'function'
    ? window.requestAnimationFrame.bind(window)
    : (next) => window.setTimeout(next, 16);

  frame(() => frame(callback));
}

function scheduleCartQuantitySync(root, { delay = 0, preferIdle = true } = {}) {
  const state = states.get(root);
  if (!state || !root.isConnected) return;

  if (state.cartSyncInFlight) {
    state.cartSyncQueued = true;
    return;
  }

  if (state.cartSyncScheduled) return;
  state.cartSyncScheduled = true;

  const run = () => {
    state.cartSyncScheduled = false;
    if (!root.isConnected || states.get(root) !== state) return;
    void syncCartQuantity(root);
  };

  const queueAfterCoreIsUsable = () => {
    if (preferIdle && typeof window.requestIdleCallback === 'function') {
      window.requestIdleCallback(run, { timeout: INITIAL_CART_SYNC_IDLE_TIMEOUT_MS });
      return;
    }

    afterCorePaint(() => window.setTimeout(run, 0));
  };

  if (delay > 0) {
    window.setTimeout(queueAfterCoreIsUsable, delay);
  } else {
    queueAfterCoreIsUsable();
  }
}

async function syncCartQuantity(root) {
  const state = states.get(root);
  if (!state || state.cartSyncInFlight) return;

  state.cartSyncInFlight = true;
  const variantId = selectedVariantId(root);
  const syncVersion = state.cartSyncVersion + 1;
  state.cartSyncVersion = syncVersion;
  let retryAfterFailure = false;

  try {
    const cart = await fetchCart();
    if (
      !root.isConnected ||
      state.cartSyncVersion !== syncVersion ||
      selectedVariantId(root) !== variantId
    ) {
      return;
    }

    state.cartQuantity = quantityInBag(cart, variantId);
    state.cartReady = true;
    state.cartSyncFailed = false;
    state.cartSyncRetryCount = 0;

    // Background cart state may change the known limits, but it must never
    // overwrite a quantity the customer selected while the request was pending.
    // normalize() refreshes ARIA/button state without mutating the input value.
  } catch {
    if (state.cartSyncVersion !== syncVersion) return;

    // Cart state is an enhancement. Keep the already-usable purchase controls
    // available and retry once in the background instead of surfacing a fatal
    // product-form state.
    state.cartSyncFailed = true;
    retryAfterFailure = state.cartSyncRetryCount < 1;
  } finally {
    state.cartSyncInFlight = false;

    if (state.cartSyncVersion === syncVersion && root.isConnected) normalize(root);

    if (state.cartSyncQueued && root.isConnected) {
      state.cartSyncQueued = false;
      scheduleCartQuantitySync(root, { preferIdle: false });
    } else if (retryAfterFailure && root.isConnected) {
      state.cartSyncRetryCount += 1;
      scheduleCartQuantitySync(root, {
        delay: CART_SYNC_RETRY_DELAY_MS,
        preferIdle: true,
      });
    }
  }
}

export async function addVariantToBag({
  variantId,
  quantity = 1,
  inventoryTracked = false,
  inventory = 0,
  source,
}) {
  const requested = Math.max(1, Math.floor(number(quantity, 1)));
  const numericVariantId = number(variantId);
  if (!numericVariantId) throw new Error(PURCHASE_MESSAGES.unavailable);

  beginPurchaseAction('add');

  let cart;
  try {
    cart = await fetchCart();
  } catch (error) {
    endPurchaseAction();
    throw error;
  }
  const currentVariantQuantity = quantityInBag(cart, numericVariantId);
  const limitRemaining = Math.max(0, PURCHASE_LIMIT_PER_VARIANT - currentVariantQuantity);

  if (requested > limitRemaining) {
    endPurchaseAction();
    throw new Error(PURCHASE_MESSAGES.limit);
  }

  const knownStockRemaining = inventoryTracked && number(inventory) > 0
    ? Math.max(0, number(inventory) - currentVariantQuantity)
    : null;

  // Never silently add fewer units than the customer requested.
  if (knownStockRemaining !== null && requested > knownStockRemaining) {
    endPurchaseAction();
    throw new Error(PURCHASE_MESSAGES.sold);
  }

  const response = await fetch(`${shopRoot()}cart/add.js`, {
    method: 'POST',
    credentials: 'same-origin',
    headers: { Accept: 'application/json', 'Content-Type': 'application/json' },
    body: JSON.stringify({ items: [{ id: numericVariantId, quantity: requested }] }),
  });
  const result = await response.json().catch(() => ({}));
  if (!response.ok) {
    endPurchaseAction();
    throw new Error(
      normalizePurchaseMessage(
        result.description || result.message,
        PURCHASE_MESSAGES.addError
      )
    );
  }

  endPurchaseAction();
  const eventTarget = source instanceof Element ? source : document;
  const quantityAfterAdd = currentVariantQuantity + requested;
  const optimisticItemCount = number(cart.item_count) + requested;

  eventTarget.dispatchEvent(
    new CartUpdateEvent(cart, source?.id || 'product-purchase', {
      source: 'product-purchase',
      itemCount: optimisticItemCount,
      variantId: String(numericVariantId),
    })
  );

  fetchCart()
    .then((updatedCart) => {
      eventTarget.dispatchEvent(
        new CartUpdateEvent(updatedCart, source?.id || 'product-purchase', {
          source: 'product-purchase-sync',
          itemCount: updatedCart.item_count,
          variantId: String(numericVariantId),
        })
      );
    })
    .catch(() => {
      // The add already succeeded. A later cart refresh can reconcile the count.
    });

  return {
    cart,
    added: requested,
    requested,
    quantityAfterAdd,
  };
}

function validateBuyNow(root) {
  const variantId = selectedVariantId(root);
  const requested = selectedQuantity(root);

  if (!variantId || !isAvailable(root)) throw new Error(PURCHASE_MESSAGES.unavailable);
  if (requested > PURCHASE_LIMIT_PER_VARIANT) throw new Error(PURCHASE_MESSAGES.limit);

  const inventory = inventoryQuantity(root);
  if (isInventoryTracked(root) && inventory > 0 && requested > inventory) {
    throw new Error(PURCHASE_MESSAGES.sold);
  }

  return { variantId, requested };
}

function redirectToSelectedItemCheckout(root) {
  const { variantId, requested } = validateBuyNow(root);
  const route = `${shopRoot()}cart/${encodeURIComponent(String(variantId))}:${requested}`;
  window.location.assign(route);
}

async function purchase(root, checkoutNow) {
  const state = states.get(root);
  if (!state || state.busy) return;

  beginPurchaseAction(checkoutNow ? 'buy-now' : 'add');

  if (!isAvailable(root)) {
    notifyPurchase(root, PURCHASE_MESSAGES.unavailable);
    endPurchaseAction();
    return;
  }

  state.busy = true;
  root.setAttribute('aria-busy', 'true');
  normalize(root);

  try {
    if (checkoutNow) {
      redirectToSelectedItemCheckout(root);
      return;
    }

    const result = await addVariantToBag({
      variantId: selectedVariantId(root),
      quantity: selectedQuantity(root),
      inventoryTracked: isInventoryTracked(root),
      inventory: inventoryQuantity(root),
      source: root,
    });

    state.cartQuantity = result.quantityAfterAdd;
    state.cartReady = true;
    state.cartSyncFailed = false;
    state.cartSyncRetryCount = 0;
    setQuantity(root, 1);
    notifyPurchase(root, PURCHASE_MESSAGES.added);
  } catch (error) {
    endPurchaseAction();
    const message = normalizePurchaseMessage(
      error instanceof Error ? error.message : '',
      PURCHASE_MESSAGES.addError
    );
    notifyPurchase(root, message);
    if (!checkoutNow) {
      scheduleCartQuantitySync(root, {
        delay: CART_SYNC_RETRY_DELAY_MS,
        preferIdle: true,
      });
    }
  } finally {
    state.busy = false;
    root.removeAttribute('aria-busy');
    normalize(root);
  }
}

function changeQuantity(root, action) {
  const next = action === 'increase' ? selectedQuantity(root) + 1 : selectedQuantity(root) - 1;
  const maximum = maximumSelectable(root);

  if (next < 1) return;
  if (Number.isFinite(maximum) && next > maximum) {
    notifyPurchase(root, maximumReachedMessage(root));
    endPurchaseAction();
    normalize(root);
    return;
  }

  setQuantity(root, next);
  normalize(root);
}

function initialize(scope = document) {
  const roots = scope instanceof Element && scope.matches(PURCHASE_ROOT)
    ? [scope]
    : scope.querySelectorAll(PURCHASE_ROOT);

  for (const root of roots) {
    if (!(root instanceof HTMLElement) || states.has(root)) continue;
    states.set(root, {
      busy: false,
      cartQuantity: 0,
      cartReady: false,
      cartSyncFailed: false,
      cartSyncInFlight: false,
      cartSyncQueued: false,
      cartSyncRetryCount: 0,
      cartSyncScheduled: false,
      cartSyncVersion: 0,
    });

    // Establish the complete interactive baseline first. Cart state is fetched
    // only after the browser has had a chance to paint or enter an idle period.
    setQuantity(root, selectedQuantity(root), { emitChange: false });
    normalize(root);
    scheduleCartQuantitySync(root);
  }
}

initialize();

window.addEventListener(
  'click',
  (event) => {
    if (!(event.target instanceof Element)) return;

    const quantityButton = event.target.closest('[data-quantity-action]');
    const purchaseButton = event.target.closest('[data-purchase-action]');
    if (!quantityButton && !purchaseButton) return;

    const root = event.target.closest(PURCHASE_ROOT);
    if (!(root instanceof HTMLElement) || !states.has(root)) return;

    event.preventDefault();
    event.stopImmediatePropagation();

    if (quantityButton) {
      const state = states.get(root);
      if (state?.cartSyncFailed) {
        state.cartSyncRetryCount = 0;
        scheduleCartQuantitySync(root, { preferIdle: false });
      }

      if (quantityButton.dataset.quantityAction === 'increase') beginPurchaseAction('quantity-plus');
      changeQuantity(root, quantityButton.dataset.quantityAction);
    } else {
      purchase(root, purchaseButton.dataset.purchaseAction === 'buy-now');
    }
  },
  true
);

window.addEventListener('online', () => {
  for (const root of document.querySelectorAll(PURCHASE_ROOT)) {
    const state = states.get(root);
    if (!state?.cartSyncFailed) continue;
    state.cartSyncRetryCount = 0;
    scheduleCartQuantitySync(root, { preferIdle: false });
  }
});

new MutationObserver((records) => {
  for (const record of records) {
    for (const node of record.addedNodes) {
      if (node instanceof Element) initialize(node);
    }
  }
}).observe(document.documentElement, { childList: true, subtree: true });

document.addEventListener(ThemeEvents.variantUpdate, (event) => {
  const html = event.detail?.data?.html;
  if (!(event.target instanceof Element) || !(html instanceof Document)) return;

  const scope =
    event.target.closest('#quick-add-modal-content') ||
    event.target.closest('.shopify-section') ||
    document;

  for (const root of scope.querySelectorAll(PURCHASE_ROOT)) {
    const blockId = root.dataset.purchaseBlockId;
    const source = Array.from(html.querySelectorAll(PURCHASE_ROOT)).find(
      (item) => !blockId || item.dataset.purchaseBlockId === blockId
    );
    if (!source) continue;

    for (const name of [
      'productId',
      'variantId',
      'variantAvailable',
      'inventoryTracked',
      'inventoryQuantity',
    ]) {
      if (source.dataset[name] === undefined) delete root.dataset[name];
      else root.dataset[name] = source.dataset[name];
    }

    for (const action of ['add', 'buy-now']) {
      const current = root.querySelector(`[data-purchase-action="${action}"]`);
      const replacement = source.querySelector(`[data-purchase-action="${action}"]`);
      if (current && replacement) current.innerHTML = replacement.innerHTML;
    }

    const state = states.get(root);
    if (state) {
      state.cartQuantity = 0;
      state.cartReady = false;
      state.cartSyncFailed = false;
      state.cartSyncRetryCount = 0;
    }

    setQuantity(root, 1, { emitChange: false });
    normalize(root);
    scheduleCartQuantitySync(root);
  }
});
